import React, { createContext, useState, useContext, useEffect, useCallback } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import {
  fetchAdminAccount,
  fetchPlugins,
  fetchDeviceSendHistory,
  getEmployeeHotels,
  getEmployeeStaff,
  getEmployeeHotelPermissions,
} from '@/apis/aipass'
import { AuthContext } from '@/contexts/auth'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { sendPrinter } from '@/utils/device'
import { setHasPrinterPlugin } from '@/libs/plugins'
import { DeviceType, SendType } from '@/models/device/device-setting'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { fetchFacilityBasicInfo } from '@/features/hotel-facility/hotel-facility-thunks'
import type { HotelPmsPermissions } from '@/models/hotel-pms-permission'
import type { EmployeeHotel } from '@/models/employee-hotel'
import { syncHotelPmsPermissionGuardState } from '@/libs/hotel-pms-permission'
import { shouldFetchHotelPmsPermissions } from '@/libs/account'

/** AccountContext のスタッフ一覧行（API 応答のうち UI / ガードで参照するフィールド） */
export type AccountEmployeeStaffRow = {
  id?: string | number
  name?: string
  isCurrent?: boolean
  hasPassCode?: boolean
}

export type AccountContextValue = {
  account: any
  fetchAdminAccount: () => Promise<void>
  basicInfo: any
  plugins: any
  setPlugins: Dispatch<SetStateAction<any>>
  employeeHotels: EmployeeHotel[]
  permissions?: HotelPmsPermissions
  permissionsLoading: boolean
  permissionsLoaded: boolean
  permissionsError: boolean
  employeeStaff: AccountEmployeeStaffRow[]
  fetchPermissions: () => Promise<HotelPmsPermissions | undefined>
  fetchEmployeeStaff: () => Promise<any[] | undefined>
  clearCurrentStaffSelection: () => void
  replaceEmployeeStaffList: (list: AccountEmployeeStaffRow[]) => void
}

const defaultAccountContextValue: AccountContextValue = {
  account: undefined,
  fetchAdminAccount: async () => {},
  basicInfo: undefined,
  plugins: undefined,
  setPlugins: () => {},
  employeeHotels: [],
  permissions: undefined,
  permissionsLoading: false,
  permissionsLoaded: false,
  permissionsError: false,
  employeeStaff: [],
  fetchPermissions: async () => undefined,
  fetchEmployeeStaff: async () => undefined,
  clearCurrentStaffSelection: () => {},
  replaceEmployeeStaffList: () => {},
}

export const AccountContext = createContext<AccountContextValue>(defaultAccountContextValue)

export const AccountProvider = ({ children }) => {
  const { errorHandler } = useErrorHandler()
  const { auth } = useContext<any>(AuthContext)
  const dispatch = useAppDispatch()
  const { basicInfo } = useAppSelector(state => state.hotelFacility)
  const [account, setAccount] = useState<any>()
  const [plugins, setPlugins] = useState<any>()
  const [employeeHotels, setEmployeeHotels] = useState<EmployeeHotel[]>([])
  const [permissions, setPermissions] = useState<HotelPmsPermissions>()
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(false)
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false)
  const [permissionsError, setPermissionsError] = useState<boolean>(false)
  const [employeeStaff, setEmployeeStaff] = useState<AccountEmployeeStaffRow[]>([])
  const hasPrinterPlugin = setHasPrinterPlugin(plugins)
  const path = window.location.pathname
  const isSelfCheckinPage = path.search(/^\/self-checkin/) === 0

  useEffect(() => {
    if (auth && !isSelfCheckinPage) {
      _fetchAdminAccount()
    }
  }, [auth])

  useEffect(() => {
    if (account && account?.hotel) {
      dispatch(fetchFacilityBasicInfo())
    }
  }, [account])

  useEffect(() => {
    if (account) {
      _fetchPlugins()
      _fetchHotels()
      // 清掃員のみ permissions 対象外。管理者も post_closing_sales_edit 等の operations を参照する。
      if (shouldFetchHotelPmsPermissions(account)) {
        fetchPermissions()
      }
      fetchEmployeeStaff()
    } else {
      setPermissions(undefined)
      setPermissionsLoading(false)
      setPermissionsLoaded(false)
      setPermissionsError(false)
      syncHotelPmsPermissionGuardState({
        account: undefined,
        permissions: undefined,
        permissionsLoaded: false,
        permissionsError: false,
      })
    }
  }, [account])

  useEffect(() => {
    syncHotelPmsPermissionGuardState({
      account,
      permissions,
      permissionsLoaded,
      permissionsError,
    })
  }, [account, permissions, permissionsLoaded, permissionsError])

  useEffect(() => {
    if (isSelfCheckinPage) {
      return
    }
    const printerSubscribeId = sessionStorage.getItem('printerSubscribeId')
    if (printerSubscribeId) {
      clearInterval(Number(sessionStorage.getItem('printerSubscribeId')))
    }
    sessionStorage.setItem(
      'printerSubscribeId',
      setInterval(() => {
        // print(30 seconds)
        _fetchDeviceSendHistory()
      }, 30000) as unknown as string,
    )
  }, [path, hasPrinterPlugin])

  const _fetchAdminAccount = async () => {
    const account = await fetchAdminAccount()
    if (account && account.hotel) {
      setAccount(account)
    }
  }

  const _fetchPlugins = async () => {
    await fetchPlugins().then(res => {
      setPlugins(res?.plugins)
    })
  }

  const _fetchDeviceSendHistory = async () => {
    const printerSettingIds = localStorage?.printerSettingIds ? JSON.parse(localStorage.printerSettingIds) : []
    if (!printerSettingIds.length) {
      return
    }

    const deviceSendHistory = await fetchDeviceSendHistory(printerSettingIds)
    if (!deviceSendHistory?.length) {
      return
    }

    for (let i = 0; i < deviceSendHistory.length; i += 1) {
      if (deviceSendHistory[i].deviceType === DeviceType.printer && deviceSendHistory[i].sendType === SendType.order) {
        await sendPrinter(deviceSendHistory[i])
      }
    }
  }

  const _fetchHotels = async () => {
    await getEmployeeHotels()
      .then(setEmployeeHotels)
      .catch(error => errorHandler(error))
  }

  const fetchPermissions = async () => {
    setPermissionsLoading(true)
    setPermissionsError(false)
    setPermissionsLoaded(false)
    try {
      const data = await getEmployeeHotelPermissions()
      setPermissions(data)
      setPermissionsLoaded(true)
      return data
    } catch (error) {
      setPermissions(undefined)
      setPermissionsError(true)
      errorHandler(error)
      return undefined
    } finally {
      setPermissionsLoading(false)
    }
  }

  /**
   * スタッフ一覧を API から再取得して Context を更新する。
   * 失敗時は `setEmployeeStaff` を呼ばない（直前の state をそのまま保持）。
   *
   * 403 STAFF_SESSION_TIMEOUT ハンドラでは、先に `clearCurrentStaffSelection` 済みのため、
   * 再取得に失敗しても **未選択のまま維持**され、古い `isCurrent` には戻らない。
   */
  const fetchEmployeeStaff = async () => {
    try {
      const data = await getEmployeeStaff()
      setEmployeeStaff(Array.isArray(data) ? (data as AccountEmployeeStaffRow[]) : [])
      return data
    } catch (error) {
      errorHandler(error)
      return undefined
    }
  }

  const clearCurrentStaffSelection = useCallback(() => {
    setEmployeeStaff(prev => (Array.isArray(prev) ? prev.map(s => ({ ...s, isCurrent: false })) : prev))
  }, [])

  const replaceEmployeeStaffList = useCallback((list: AccountEmployeeStaffRow[]) => {
    setEmployeeStaff(list)
  }, [])

  return (
    <AccountContext.Provider
      value={{
        account,
        fetchAdminAccount: _fetchAdminAccount,
        basicInfo,
        plugins,
        setPlugins,
        employeeHotels,
        permissions,
        permissionsLoading,
        permissionsLoaded,
        permissionsError,
        fetchPermissions,
        employeeStaff,
        fetchEmployeeStaff,
        clearCurrentStaffSelection,
        replaceEmployeeStaffList,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}
